import React, { useState } from 'react';
import {
  Box,
  Typography,
  ListItem,
  IconButton,
  Collapse,
  Divider,
  Tooltip
} from '@mui/material';
import {
  Map as MapIcon,
  MoreVert as MoreVertIcon,
  Straighten as DistanceIcon,
  Landscape as MountainsIcon,
  Schedule as DurationIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon
} from '@mui/icons-material';
import TrekkingIcon from '../../assets/bike_profile_icons/BikeProfile_Trekking_light.svg';
import RoadBikeIcon from '../../assets/bike_profile_icons/BikeProfile_RoadBike_light.svg';
import GravelBikeIcon from '../../assets/bike_profile_icons/BikeProfile_GravelBike_light.svg';
import MountainBikeIcon from '../../assets/bike_profile_icons/BikeProfile_MountainBike_light.svg';
import { useAddress } from '../../hooks/useAddress';
import { ThumbnailsDisplay } from './ThumbnailsDisplay';
import type { Route, RoutePoint } from '../../types/storage';
import { formatAddressFromRoutePoint } from '../../utils/geocodingConverter';

interface RouteListItemProps {
  route: Route;
  onLoadRoute: (route: Route) => void;
  onMenuOpen: (event: React.MouseEvent<HTMLElement>, route: Route) => void;
  onThumbnailClick?: (route: Route, type?: 'route' | 'elevation') => void;
  isLast: boolean;
}

const getBikeProfileIcon = (profile: string) => {  
  switch (profile) {
    case 'road':
      return <img src={RoadBikeIcon} alt="Road Bike" style={{ height: 36, aspectRatio: 'auto' }} />;
    case 'mtb':
      return <img src={MountainBikeIcon} alt="Mountain Bike" style={{ height: 36, aspectRatio: 'auto' }} />;
    case 'gravel':
      return <img src={GravelBikeIcon} alt="Gravel Bike" style={{ height: 36, aspectRatio: 'auto' }} />;
    case 'trekking':
      return <img src={TrekkingIcon} alt="Trekking" style={{ height: 36, aspectRatio: 'auto' }} />;
    case 'fastbike':
      return <img src={RoadBikeIcon} alt="Road Bike" style={{ height: 36, aspectRatio: 'auto' }} />;
    case 'bike': // Fallback for old "bike" entries, treat as trekking
    default:
      return <img src={TrekkingIcon} alt="Trekking" style={{ height: 36, aspectRatio: 'auto' }} />;
  }
};

const formatDate = (date: Date | undefined) => {
  if (!date) return 'Unknown date';
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatDistance = (distanceInMeters: number | undefined): string => {
  if (!distanceInMeters) return 'N/A';
  const km = distanceInMeters / 1000;
  return `${km.toFixed(1)} km`;
};

const formatDuration = (durationInSeconds: number | undefined): string => {
  if (!durationInSeconds) return 'N/A';
  const hours = Math.floor(durationInSeconds / 3600);
  const minutes = Math.floor((durationInSeconds % 3600) / 60);
  
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
};

const formatElevation = (elevationInMeters: number | undefined): string => {
  if (!elevationInMeters) return 'N/A';
  return `${Math.round(elevationInMeters)}m`;
};

const formatDateInfo = (route: Route): string => {
  if (!route.created) return 'Unknown date';
  
  // Check if route has been updated (more than 1 minute after creation)
  if (route.updated) {
    const timeDifference = route.updated.getTime() - route.created.getTime();
    if (timeDifference > 60000) { // More than 1 minute difference
      return `Created ${formatDate(route.created)} • Updated ${formatDate(route.updated)}`;
    }
  }
  
  // Default: show only creation date
  return `Created ${formatDate(route.created)}`;
};

// Separate memoized component for the lock icon to prevent thumbnail flickering
const VisibilityIndicator: React.FC<{ isPublic: boolean }> = React.memo(({ isPublic }) => {
  const [tooltipOpen, setTooltipOpen] = useState(false);

  // Handle touch events for tooltip on mobile
  const handleTooltipTouch = () => {
    // Don't use stopPropagation in touch handler to avoid passive event listener warning
    setTooltipOpen(!tooltipOpen);
    
    // Auto-close tooltip after 2 seconds
    setTimeout(() => {
      setTooltipOpen(false);
    }, 2000);
  };

  const handleTooltipClose = () => {
    setTooltipOpen(false);
  };

  return (
    <Tooltip 
      title={isPublic ? 'Public route - visible to everyone' : 'Private route - only visible to you'} 
      arrow 
      placement="top"
      open={tooltipOpen}
      onClose={handleTooltipClose}
      disableFocusListener
      disableHoverListener={tooltipOpen} // Disable hover when manually controlled
      disableTouchListener
    >
      <Box
        onTouchStart={handleTooltipTouch}
        onClick={(e) => {
          e.stopPropagation(); // Prevent triggering the expand click
        }}
        sx={{ 
          display: 'flex', 
          alignItems: 'center',
          cursor: 'pointer',
          '&:hover': {
            opacity: 0.8
          }
        }}
      >
        {isPublic ? (
          <LockOpenIcon 
            sx={{ 
              fontSize: '1rem', 
              color: 'success.main',
              opacity: 0.7
            }} 
          />
        ) : (
          <LockIcon 
            sx={{ 
              fontSize: '1rem', 
              color: 'text.secondary',
              opacity: 0.5
            }} 
          />
        )}
      </Box>
    </Tooltip>
  );
});

const RouteEndpoint: React.FC<{ 
  routePoint: RoutePoint | null; 
  label: string;
}> = ({ routePoint, label }) => {
  // Try to use stored address data first, fall back to reverse geocoding
  const storedAddress = routePoint ? formatAddressFromRoutePoint(routePoint) : null;
  const position = routePoint ? { lat: routePoint.lat, lng: routePoint.lng } : null;
  
  // Only use reverse geocoding if we don't have stored address data
  const { address: geocodedAddress } = useAddress(
    !storedAddress && position ? position : null
  );

  if (!routePoint) return null;

  const displayAddress = storedAddress || geocodedAddress;

  return (
    <Box sx={{ mb: 0.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', fontWeight: 500 }}>
        {label}:
      </Typography>
      <Typography variant="body2" sx={{ fontSize: '0.75rem', ml: 0.5, color: 'text.primary' }}>
        {displayAddress || `${routePoint.lat.toFixed(4)}, ${routePoint.lng.toFixed(4)}`}
      </Typography>
    </Box>
  );
};

export const RouteListItem: React.FC<RouteListItemProps> = ({
  route,
  onLoadRoute,
  onMenuOpen,
  onThumbnailClick,
  isLast
}) => {
  const [expanded, setExpanded] = useState(false);
  const startPoint = route.points.find(p => p.orderIndex === 0) || route.points[0];
  const endPoint = route.points.find(p => p.orderIndex === route.points.length - 1) || route.points[route.points.length - 1];

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  return (
    <>
      <ListItem
        onClick={handleExpandClick}
        sx={{ 
          py: 1,
          px: 1, // Reduced from 2 to 1
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          gap: 1,
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: 'action.hover'
          }
        }}
      >
        {/* Route title row with bike icon and action buttons */}
        <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ width: '100%' }}>
          <Box display="flex" alignItems="center" gap={1} sx={{ flex: 1, minWidth: 0 }}>
            {getBikeProfileIcon(route.bikeProfile)}
            <Tooltip title={route.name} arrow placement="top">
              <Typography 
                variant="subtitle1" 
                component="span" 
                sx={{ 
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  lineHeight: 1.2,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  cursor: 'pointer'
                }}
              >
                {route.name}
              </Typography>
            </Tooltip>
            
            {/* Visibility indicator */}
            <VisibilityIndicator isPublic={route.isPublic || false} />
          </Box>
          
          <Box display="flex" alignItems="center" gap={0.5} sx={{ flexShrink: 0 }}>
            <IconButton
              edge="end"
              aria-label="load route"
              onClick={(e) => {
                e.stopPropagation();
                onLoadRoute(route);
              }}
              color="primary"
              size="small"
              sx={{ 
                '&:hover': { 
                  backgroundColor: 'primary.light',
                  color: 'primary.contrastText'
                }
              }}
            >
              <MapIcon fontSize="small" />
            </IconButton>
            <IconButton
              edge="end"
              aria-label="more options"
              onClick={(e) => {
                e.stopPropagation();
                onMenuOpen(e, route);
              }}
              size="small"
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {/* Content row with thumbnail and stats */}
        <Box display="flex" alignItems="stretch" gap={2} sx={{ width: '100%' }}>
          {/* Route Thumbnails */}
          <ThumbnailsDisplay
            route={route}
            size="medium"
            onThumbnailClick={onThumbnailClick}
            layout="horizontal"
          />

          {/* Stats taking full available width */}
          <Box 
            display="flex" 
            alignItems="center" 
            justifyContent="space-evenly"
            sx={{ 
              flex: 1, 
              minWidth: 0,
              overflow: 'hidden',
              width: '100%'
            }}
          >
            <Box display="flex" flexDirection="column" alignItems="center" gap={0.5} sx={{ flex: 1 }}>
              <DistanceIcon sx={{ fontSize: 24, color: 'text.secondary' }} />
              <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', fontWeight: 500 }}>
                Distance
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  fontWeight: 500, 
                  color: 'text.primary',
                  textAlign: 'center',
                  whiteSpace: 'nowrap'
                }}
              >
                {formatDistance(route.distance)}
              </Typography>
            </Box>
            <Box display="flex" flexDirection="column" alignItems="center" gap={0.5} sx={{ flex: 1 }}>
              <MountainsIcon sx={{ fontSize: 24, color: 'text.secondary' }} />
              <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', fontWeight: 500 }}>
                Elevation
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  fontWeight: 500, 
                  color: 'text.primary',
                  textAlign: 'center',
                  whiteSpace: 'nowrap'
                }}
              >
                {formatElevation(route.ascend)}
              </Typography>
            </Box>
            <Box display="flex" flexDirection="column" alignItems="center" gap={0.5} sx={{ flex: 1 }}>
              <DurationIcon sx={{ fontSize: 24, color: 'text.secondary' }} />
              <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', fontWeight: 500 }}>
                Duration
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  fontWeight: 500, 
                  color: 'text.primary',
                  textAlign: 'center',
                  whiteSpace: 'nowrap'
                }}
              >
                {formatDuration(route.duration)}
              </Typography>
            </Box>
          </Box>
        </Box>
      </ListItem>
      
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ px: 1, pb: 2 }}>
          <Divider sx={{ mb: 1.5 }} />
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', fontWeight: 500, mb: 1.5, display: 'block' }}>
            Route Details
          </Typography>
          
          {/* Grid layout - 2 columns */}
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: '1fr 1fr', 
            gap: 1, // Reduced from 2 to 1 to decrease spacing between rows
            width: '100%'
          }}>
            
            {/* First Row - Start Point (Left Column) */}
            <Box>
              <RouteEndpoint 
                routePoint={startPoint}
                label="Start"
              />
            </Box>
            
            {/* First Row - End Point (Right Column) */}
            <Box>
              <RouteEndpoint 
                routePoint={endPoint}
                label="End"
              />
            </Box>
            
            {/* Second Row - Elevation Chart (Both Columns Merged) */}
            {(route.elevation_thumbnail_path || (route.ascend && route.ascend > 0)) && (
              <Box sx={{ 
                gridColumn: '1 / -1', // Span all columns
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start', // Keep header left-aligned
                mt: 1,
                width: '100%' // Ensure full width usage
              }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', fontWeight: 500, mb: 1, display: 'block' }}>
                  Elevation Profile
                </Typography>
                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                  <ThumbnailsDisplay
                    route={route}
                    size="xlarge"
                    onThumbnailClick={onThumbnailClick}
                    layout="vertical"
                    showElevationOnly={true}
                  />
                </Box>
              </Box>
            )}
            
            {/* Third Row - Creation Date (Both Columns Merged) */}
            <Box sx={{ 
              gridColumn: '1 / -1', // Span all columns
              textAlign: 'left', // Changed from 'center' to 'left' for left alignment
              mt: 0.25 // Further reduced from 0.5 to 0.25 to minimize spacing
            }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                {formatDateInfo(route)}
              </Typography>
            </Box>
            
          </Box>
        </Box>
      </Collapse>
      
      {!isLast && <Divider />}
    </>
  );
};
